import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { convertToAmPm } from '../../../config/utility';
import api from '../../../api/api';

const SlotSelector = ({ state, dispatch, serviceType }) => {
  // Use the appropriate initial slot from the reducer state.
  const initialSlot = serviceType === 'taxi' ? state.taxiSlot : state.walkSlot;
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(initialSlot || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchSlots = async () => {
      if (!state.day) return; // Only fetch if a day has been selected.
      setSelectedSlot(initialSlot || null);
      setLoading(true);
      setError(false);
      try {
        const response = await api.get('/get-availability', {
          params: {
            services: state.services,
            day: state.day,
            duration: state.duration_walk,
          },
        });
        if (response.data.slots) {
          setSlots(response.data.slots);
          dispatch({ type: 'UPDATE_FIELD', field: 'slot_token', payload: response.data.token });
        } else {
          setSlots([]);
          setError(true);
        }
      } catch (err) {
        // Error fetching slots
      } finally {
        setLoading(false);
      }
    };

    fetchSlots();
  }, [state.day, state.duration_walk, state.services, dispatch]);

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    if (serviceType === 'taxi') {
      dispatch({ type: 'SET_TAXI_SLOT', payload: slot });
    } else if (serviceType === 'walk') {
      dispatch({ type: 'SET_WALK_SLOT', payload: slot });
    }
  };

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-8">
      {/* Header Section */}
      <h2 className="text-lg sm:text-2xl font-semibold text-purple-700 text-center mb-2 sm:mb-4">
        {serviceType === 'taxi'
          ? 'When would you like your taxi service? ✨'
          : 'Select your pet\'s walk slot for a fun outing! 🐾'}
      </h2>
      <p className="text-sm sm:text-base text-center text-gray-600 mb-4 sm:mb-6">
        {serviceType === 'taxi'
          ? 'Pick a time slot that fits your schedule, and we\'ll take care of the ride!'
          : 'Choose the perfect time for your furry friend\'s walk.'}
      </p>

      {loading ? (
        <div className="flex items-center justify-center">
          <p className="text-sm sm:text-base text-center text-gray-500 animate-pulse px-4">
            🔄 Hold on a sec! We're fetching the best slots for you...
          </p>
        </div>
      ) : error || slots.length === 0 ? (
        <div className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4 rounded-r-md">
          <p className="text-sm sm:text-base text-center text-red-600 font-medium">
            🚫 Uh-oh! No slots available for this day.
            <span className="block mt-1 text-xs sm:text-sm font-normal">
              Try choosing a different day for more options.
            </span>
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
          {slots.map((slot, index) => (
            <motion.div
              key={index}
              className={`p-3 sm:p-4 rounded-lg cursor-pointer border-2 
                transition-all duration-300 shadow-sm hover:shadow-md
                ${selectedSlot === slot
                  ? 'bg-gradientEnd text-white border-gradientEnd'
                  : 'bg-bodyMain text-dark border-gray-200 hover:border-gradientEnd'
                }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleSlotSelect(slot)}
            >
              <p className="text-center text-sm sm:text-base font-medium">
                🕒 {convertToAmPm(slot)}
              </p>
            </motion.div>
          ))}
        </div>
      )}

      {selectedSlot && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 sm:mt-6 text-center space-y-2"
        >
          <p className="text-secondary text-base sm:text-lg font-medium">
            ✅ You've reserved:
            <span className="text-primary font-bold ml-1">
              {convertToAmPm(selectedSlot)}
            </span>
          </p>
          <p className="text-xs sm:text-sm text-gray-500">
            No worries—you can update this before finalizing your booking.
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default SlotSelector;
